import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import VisibilityIcon from '@material-ui/icons/Visibility';
import moment from 'moment';
import colors from 'config/colors';
import { employeeStatic } from 'config/constants';
import { fetchViewCountService, fetchViewersService } from 'actions/admin/contentView';
import EmptyState from './EmptyState';
import { SkeletonRows } from './Loader';
import CustomButton from 'components/CustomButton';

const PAGE_SIZE = 50;
const ROLE_LOOKS = {
  ROLE_GUARD: { label: 'Guardian', color: '#6a1b9a', background: '#f3e5f5' },
  ROLE_TEACHER: { label: employeeStatic, color: '#1565c0', background: '#e3f2fd' },
};
const lookFor = role => ROLE_LOOKS[role] || { label: 'Other', color: '#546e7a', background: '#eceff1' };

// Everyone who has seen this content: a name, who they are (guardian or teacher), the students of a guardian and when.
export const ViewersDialog = ({ isOpen, onClose, contentType, contentId, title }) => {
  const token = useSelector(state => state.auth.loggedUserToken);
  const [viewers, setViewers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('ALL');

  const load = async pageNo => {
    setLoading(true);
    try {
      const list = await fetchViewersService({ token, contentType, contentId, pageNo, pageSize: PAGE_SIZE });
      setViewers(prev => (pageNo === 1 ? list : [...prev, ...list]));
      setPage(pageNo);
      setHasMore(list.length >= PAGE_SIZE);
    } catch (err) {
      setViewers([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      setFilter('ALL');
      load(1);
    }
  }, [isOpen, contentType, contentId]);

  const counts = viewers.reduce((acc, v) => ({ ...acc, [v.userRole]: (acc[v.userRole] || 0) + 1 }), {});
  const shown = filter === 'ALL' ? viewers : viewers.filter(v => v.userRole === filter);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md" scrollable>
      <ModalHeader toggle={onClose}>
        <span style={{ fontSize: 16, whiteSpace: 'nowrap' }}>{`Viewed by ${viewers.length}${hasMore ? '+' : ''}`}</span>
        {!!title && (
          <div className="text-muted" style={{ fontSize: 12, fontWeight: 'normal', marginTop: 2 }}>
            {title}
          </div>
        )}
      </ModalHeader>
      <ModalBody>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {[
            { key: 'ALL', label: 'All', count: viewers.length, color: colors.accent },
            {
              key: 'ROLE_GUARD',
              label: 'Guardians',
              count: counts.ROLE_GUARD || 0,
              color: ROLE_LOOKS.ROLE_GUARD.color,
            },
            {
              key: 'ROLE_TEACHER',
              label: `${employeeStatic}s`,
              count: counts.ROLE_TEACHER || 0,
              color: ROLE_LOOKS.ROLE_TEACHER.color,
            },
          ].map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setFilter(chip.key)}
              style={{
                border: `1px solid ${filter === chip.key ? chip.color : '#e2d9ea'}`,
                background: filter === chip.key ? chip.color : '#fff',
                color: filter === chip.key ? '#fff' : chip.color,
                borderRadius: 14,
                padding: '1px 12px',
                fontSize: 12,
                fontWeight: 'bold',
                cursor: 'pointer',
              }}>
              {chip.label} {chip.count}
            </button>
          ))}
        </div>
        {loading && !viewers.length ? (
          <SkeletonRows rows={4} />
        ) : shown.length ? (
          shown.map(viewer => {
            const look = lookFor(viewer.userRole);
            return (
              <div
                key={`${viewer.userId}-${viewer.userRole}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 2px',
                  borderBottom: '1px solid #f0eaf5',
                }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: look.background,
                    color: look.color,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {(viewer.name || '?').trim().charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {viewer.name || 'Unknown'}
                    <span
                      style={{
                        marginLeft: 8,
                        padding: '0 8px',
                        borderRadius: 10,
                        fontSize: 10.5,
                        fontWeight: 'bold',
                        color: look.color,
                        background: look.background,
                      }}>
                      {look.label}
                    </span>
                  </div>
                  {!!viewer.students?.length && (
                    <div style={{ fontSize: 12, color: colors.grey3 }}>
                      Students: {viewer.students.map(student => student.name).join(', ')}
                    </div>
                  )}
                </div>
                <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                  {viewer.viewedDateTime ? moment(viewer.viewedDateTime).format('DD MMM, hh:mm A') : ''}
                </small>
              </div>
            );
          })
        ) : (
          <EmptyState
            compact
            bare
            icon="far fa-eye-slash"
            title="No views yet"
            subtitle="Nobody has opened this yet."
          />
        )}
        {hasMore && (
          <div className="text-center mt-3">
            <CustomButton
              variant="outlined"
              size="small"
              name={loading ? 'Loading...' : 'View more'}
              onClick={() => !loading && load(page + 1)}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

// "👁 12 views": how many have seen this content. A click opens the list of who. Shown to the logins that may read it (admin,
// dean, teacher); anything else (or a failed request) shows nothing.
export const ViewsButton = ({ contentType, contentId, title, style }) => {
  const token = useSelector(state => state.auth.loggedUserToken);
  const [count, setCount] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCount(null);
    if (contentId) {
      fetchViewCountService({ token, contentType, contentId })
        .then(value => !cancelled && setCount(value))
        .catch(() => !cancelled && setCount(-1));
    }
    return () => {
      cancelled = true;
    };
  }, [contentType, contentId, token]);

  if (count === -1 || !contentId) return null;
  return (
    <>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        title="See who viewed this"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          border: `1px solid ${colors.accent}44`,
          background: `${colors.accent}0f`,
          color: colors.accentDark,
          borderRadius: 14,
          padding: '2px 10px',
          fontSize: 12,
          fontWeight: 'bold',
          cursor: 'pointer',
          ...style,
        }}>
        <VisibilityIcon style={{ fontSize: 15 }} />
        {count === null ? '...' : `${count} ${count === 1 ? 'view' : 'views'}`}
      </button>
      <ViewersDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        contentType={contentType}
        contentId={contentId}
        title={title}
      />
    </>
  );
};

export default ViewsButton;
